/**
 * AI Chat Widget Component
 * 
 * A floating chatbot widget that provides AI-powered assistance
 * for querying and managing patient data.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Fab,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Collapse,
  Chip,
  CircularProgress,
  Tooltip,
  Zoom,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Person as PersonIcon,
  Lightbulb as TipIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { sendMessageStream, getCapabilities, SAMPLE_PROMPTS } from '../../services/aiService';
import { useUserData } from '../../hooks/useUserData';

/**
 * Formats AI response text into styled React elements
 */
const FormattedMessage = ({ text, isUser }) => {
  const theme = useTheme();
  
  if (!text) return null;
  
  const formatText = (content) => {
    const lines = content.split('\n');
    const elements = [];
    let currentList = [];
    let listType = null;
    let tableRows = [];
    let inTable = false;
    
    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(
          <Box 
            key={`list-${elements.length}`} 
            component={listType === 'numbered' ? 'ol' : 'ul'}
            sx={{ 
              m: 0, 
              pl: 2.5, 
              py: 0.5,
              '& li': { 
                mb: 0.5,
                '&::marker': {
                  color: isUser ? 'rgba(255,255,255,0.8)' : theme.palette.primary.main,
                  fontWeight: 600,
                },
              },
            }}
          >
            {currentList.map((item, idx) => (
              <li key={idx}>
                <Typography variant="body2" component="span" sx={{ lineHeight: 1.5 }}>
                  {formatInlineText(item)}
                </Typography>
              </li>
            ))}
          </Box>
        );
        currentList = [];
        listType = null;
      }
    };
    
    const flushTable = () => {
      if (tableRows.length > 0) {
        const headers = tableRows[0];
        const dataRows = tableRows.slice(2);
        
        elements.push(
          <Box 
            key={`table-${elements.length}`}
            sx={{ 
              overflowX: 'auto', 
              my: 1,
              borderRadius: 1,
              border: `1px solid ${isUser ? 'rgba(255,255,255,0.2)' : theme.palette.divider}`,
            }}
          >
            <Box component="table" sx={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '0.8rem',
            }}>
              <Box component="thead">
                <Box component="tr" sx={{ 
                  bgcolor: isUser ? 'rgba(255,255,255,0.1)' : alpha(theme.palette.primary.main, 0.08),
                }}>
                  {headers.map((cell, idx) => (
                    <Box 
                      component="th" 
                      key={idx}
                      sx={{ 
                        p: 1, 
                        textAlign: 'left',
                        fontWeight: 600,
                        borderBottom: `1px solid ${isUser ? 'rgba(255,255,255,0.2)' : theme.palette.divider}`,
                      }}
                    >
                      {cell.trim()}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {dataRows.map((row, rowIdx) => (
                  <Box 
                    component="tr" 
                    key={rowIdx}
                    sx={{
                      '&:nth-of-type(even)': {
                        bgcolor: isUser ? 'rgba(255,255,255,0.05)' : alpha(theme.palette.grey[100], 0.5),
                      },
                    }}
                  >
                    {row.map((cell, cellIdx) => (
                      <Box 
                        component="td" 
                        key={cellIdx}
                        sx={{ 
                          p: 1,
                          borderBottom: rowIdx < dataRows.length - 1 
                            ? `1px solid ${isUser ? 'rgba(255,255,255,0.1)' : theme.palette.divider}` 
                            : 'none',
                        }}
                      >
                        {formatInlineText(cell.trim())}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        );
        tableRows = [];
        inTable = false;
      }
    };
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();
      
      if (trimmedLine.startsWith('|') && trimmedLine.endsWith('|')) {
        flushList();
        inTable = true;
        const cells = trimmedLine.slice(1, -1).split('|');
        if (!trimmedLine.match(/^\|[\s-:|]+\|$/)) {
          tableRows.push(cells);
        } else {
          tableRows.push(null);
        }
        continue;
      } else if (inTable) {
        flushTable();
      }
      
      if (trimmedLine.startsWith('### ')) {
        flushList();
        elements.push(
          <Typography 
            key={`h3-${i}`} 
            variant="subtitle2" 
            sx={{ 
              fontWeight: 700, 
              mt: elements.length > 0 ? 1.5 : 0, 
              mb: 0.5,
              color: isUser ? 'white' : theme.palette.primary.main,
            }}
          >
            {formatInlineText(trimmedLine.slice(4))}
          </Typography>
        );
        continue;
      }
      
      if (trimmedLine.startsWith('## ')) {
        flushList();
        elements.push(
          <Typography 
            key={`h2-${i}`} 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 700, 
              mt: elements.length > 0 ? 1.5 : 0, 
              mb: 0.5,
              color: isUser ? 'white' : theme.palette.primary.main,
            }}
          >
            {formatInlineText(trimmedLine.slice(3))}
          </Typography>
        );
        continue;
      }
      
      const bulletMatch = trimmedLine.match(/^[-*•]\s+(.+)$/);
      if (bulletMatch) {
        if (listType !== 'bullet') {
          flushList();
          listType = 'bullet';
        }
        currentList.push(bulletMatch[1]);
        continue;
      }
      
      const numberedMatch = trimmedLine.match(/^(\d+)\.\s+(.+)$/);
      if (numberedMatch) {
        if (listType !== 'numbered') {
          flushList();
          listType = 'numbered';
        }
        currentList.push(numberedMatch[2]);
        continue;
      }
      
      if (trimmedLine === '') {
        flushList();
        continue;
      }
      
      flushList();
      elements.push(
        <Typography 
          key={`p-${i}`} 
          variant="body2" 
          sx={{ 
            mb: 0.75, 
            lineHeight: 1.6,
            '&:last-child': { mb: 0 },
          }}
        >
          {formatInlineText(trimmedLine)}
        </Typography>
      );
    }
    
    flushList();
    flushTable();
    
    return elements;
  };
  
  const formatInlineText = (text) => {
    if (!text) return text;
    
    const parts = [];
    let remaining = text;
    let keyCounter = 0;
    
    while (remaining.length > 0) {
      const boldMatch = remaining.match(/\*\*(.+?)\*\*|__(.+?)__/);
      const codeMatch = remaining.match(/`([^`]+)`/);
      
      let earliestMatch = null;
      let matchType = null;
      
      if (boldMatch && (!earliestMatch || boldMatch.index < earliestMatch.index)) {
        earliestMatch = boldMatch;
        matchType = 'bold';
      }
      if (codeMatch && (!earliestMatch || codeMatch.index < earliestMatch.index)) {
        earliestMatch = codeMatch;
        matchType = 'code';
      }
      
      if (earliestMatch) {
        if (earliestMatch.index > 0) {
          parts.push(remaining.slice(0, earliestMatch.index));
        }
        
        const content = earliestMatch[1] || earliestMatch[2];
        if (matchType === 'bold') {
          parts.push(
            <Box 
              component="strong" 
              key={`bold-${keyCounter++}`}
              sx={{ fontWeight: 600 }}
            >
              {content}
            </Box>
          );
        } else if (matchType === 'code') {
          parts.push(
            <Box 
              component="code" 
              key={`code-${keyCounter++}`}
              sx={{ 
                px: 0.5, 
                py: 0.25, 
                borderRadius: 0.5, 
                bgcolor: isUser ? 'rgba(255,255,255,0.15)' : alpha(theme.palette.grey[200], 0.8),
                fontFamily: 'monospace',
                fontSize: '0.85em',
              }}
            >
              {content}
            </Box>
          );
        }
        
        remaining = remaining.slice(earliestMatch.index + earliestMatch[0].length);
      } else {
        parts.push(remaining);
        break;
      }
    }
    
    return parts.length === 1 ? parts[0] : parts;
  };
  
  return <Box>{formatText(text)}</Box>;
};

// Message component
const ChatMessage = ({ message, isUser, isTyping }) => {
  const theme = useTheme();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: isUser ? 'flex-end' : 'flex-start',
          mb: 1.5,
          px: 1,
        }}
      >
        {!isUser && (
          <Avatar
            src="/bolt-logo-chatbot.png"
            alt="Bolt AI"
            sx={{
              width: 32,
              height: 32,
              mr: 1,
              bgcolor: 'transparent',
              flexShrink: 0,
              alignSelf: 'flex-start',
              mt: 0.5,
            }}
          />
        )}
        <Paper
          elevation={0}
          sx={{
            maxWidth: '85%',
            p: 1.5,
            borderRadius: 2,
            bgcolor: isUser 
              ? theme.palette.primary.main 
              : alpha(theme.palette.grey[50], 0.95),
            color: isUser ? 'white' : 'text.primary',
            borderTopRightRadius: isUser ? 4 : 16,
            borderTopLeftRadius: isUser ? 16 : 4,
            boxShadow: isUser ? 'none' : `0 1px 3px ${alpha(theme.palette.common.black, 0.08)}`,
          }}
        >
          {isTyping ? (
            <Box sx={{ display: 'flex', gap: 0.5, py: 0.5 }}>
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  animate={{ y: [0, -4, 0] }}
                  transition={{ repeat: Infinity, duration: 0.6, delay: i * 0.1 }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: theme.palette.grey[400],
                    }}
                  />
                </motion.div>
              ))}
            </Box>
          ) : (
            <FormattedMessage text={message} isUser={isUser} />
          )}
        </Paper>
        {isUser && (
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: theme.palette.secondary.main,
              ml: 1,
              flexShrink: 0,
              alignSelf: 'flex-start',
              mt: 0.5,
            }}
          >
            <PersonIcon sx={{ fontSize: 18 }} />
          </Avatar>
        )}
      </Box>
    </motion.div>
  );
};

// Sample prompts section
const SamplePrompts = ({ onSelectPrompt, canPerformActions }) => {
  const [expanded, setExpanded] = useState(true);
  const theme = useTheme();
  
  const prompts = canPerformActions 
    ? [...SAMPLE_PROMPTS.queries.slice(0, 3), ...SAMPLE_PROMPTS.actions.slice(0, 2)]
    : SAMPLE_PROMPTS.queries.slice(0, 5);
  
  return (
    <Box sx={{ px: 2, pb: 1 }}>
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          cursor: 'pointer',
          mb: 1,
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <TipIcon sx={{ fontSize: 16, mr: 0.5, color: theme.palette.warning.main }} />
        <Typography variant="caption" color="text.secondary">
          Try these prompts
        </Typography>
        {expanded ? (
          <CollapseIcon sx={{ fontSize: 16, ml: 'auto' }} />
        ) : (
          <ExpandIcon sx={{ fontSize: 16, ml: 'auto' }} />
        )}
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {prompts.map((prompt, index) => (
            <Chip
              key={index}
              label={prompt.length > 35 ? `${prompt.slice(0, 35)}...` : prompt}
              size="small"
              variant="outlined"
              onClick={() => onSelectPrompt(prompt)}
              sx={{
                fontSize: '0.7rem',
                height: 24,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  borderColor: theme.palette.primary.main,
                },
              }}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

// Main chat widget component
const AIChatWidget = ({ 
  position = 'bottom-right',
  defaultOpen = false,
}) => {
  const theme = useTheme();
  const { user } = useUserData();
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [capabilities, setCapabilities] = useState(null);
  const [streamingMessage, setStreamingMessage] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const responseAddedRef = useRef(false);
  
  useEffect(() => {
    if (user && isOpen) {
      loadCapabilities();
    }
  }, [user, isOpen]);
  
  const loadCapabilities = async () => {
    try {
      const caps = await getCapabilities();
      setCapabilities(caps);
    } catch (error) {
      console.error('Failed to load AI capabilities:', error);
    }
  };
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingMessage]);
  
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);
  
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: `Hello! I'm Bolt, your intelligent EMR assistant. I can help you:\n\n• Search for patients and view their information\n• Check appointments and schedules\n• View encounters and clinical data\n• Look up staff availability\n\nHow can I help you today?`,
      }]);
    }
  }, [isOpen, messages.length]);
  
  const handleSendMessage = useCallback(async () => {
    if (!inputValue.trim() || isLoading) return;
    
    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: inputValue.trim(),
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setStreamingMessage('');
    
    const conversationHistory = messages.slice(-10).map(m => ({
      role: m.role,
      content: m.content,
    }));
    
    try {
      let fullResponse = '';
      responseAddedRef.current = false;
      
      // Get the user's current facility ID for proper data scoping
      const currentFacilityId = typeof user?.defaultFacility === 'string' 
        ? user.defaultFacility 
        : user?.defaultFacility?.id;
      
      await sendMessageStream(
        userMessage.content,
        conversationHistory,
        (chunk) => {
          fullResponse += chunk;
          setStreamingMessage(fullResponse);
        },
        () => {
          if (responseAddedRef.current) return;
          responseAddedRef.current = true;
          const newMessage = {
            id: `assistant-${Date.now()}`,
            role: 'assistant',
            content: fullResponse,
          };
          setMessages(prev => [...prev, newMessage]);
          setStreamingMessage('');
          setIsLoading(false);
        },
        (error) => {
          if (responseAddedRef.current) return;
          responseAddedRef.current = true;
          console.error('Stream error:', error);
          setMessages(prev => [...prev, {
            id: `error-${Date.now()}`,
            role: 'assistant',
            content: 'Sorry, I encountered an error. Please try again.',
          }]);
          setStreamingMessage('');
          setIsLoading(false);
        },
        currentFacilityId // Pass current facility for proper data scoping
      );
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Sorry, I couldn\'t process your request. Please try again.',
      }]);
      setIsLoading(false);
    }
  }, [inputValue, isLoading, messages]);
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  const handleSelectPrompt = (prompt) => {
    setInputValue(prompt);
    inputRef.current?.focus();
  };
  
  const handleClearChat = () => {
    setMessages([]);
    setStreamingMessage('');
  };
  
  if (!user) {
    return null;
  }
  
  const positionStyles = {
    'bottom-right': { bottom: 24, right: 24 },
    'bottom-left': { bottom: 24, left: 24 },
    'top-right': { top: 24, right: 24 },
    'top-left': { top: 24, left: 24 },
  };
  
  return (
    <Box
      sx={{
        position: 'fixed',
        zIndex: 1300,
        ...positionStyles[position],
      }}
    >
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            <Paper
              elevation={8}
              sx={{
                width: 380,
                height: 520,
                mb: 2,
                borderRadius: 3,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.paper',
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: theme.palette.primary.main,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Avatar 
                  src="/bolt-logo-chatbot.png" 
                  alt="Bolt AI"
                  sx={{ width: 28, height: 28, mr: 1, bgcolor: 'transparent' }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Bolt
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    AI Assistant
                  </Typography>
                </Box>
                <Tooltip title="Clear chat">
                  <IconButton 
                    size="small" 
                    onClick={handleClearChat}
                    sx={{ color: 'white', mr: 0.5 }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close">
                  <IconButton 
                    size="small" 
                    onClick={() => setIsOpen(false)}
                    sx={{ color: 'white' }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              
              {/* Messages */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  py: 2,
                  bgcolor: alpha(theme.palette.grey[50], 0.5),
                }}
              >
                {messages.map((msg) => (
                  <ChatMessage
                    key={msg.id}
                    message={msg.content}
                    isUser={msg.role === 'user'}
                  />
                ))}
                
                {streamingMessage && (
                  <ChatMessage
                    message={streamingMessage}
                    isUser={false}
                  />
                )}
                
                {isLoading && !streamingMessage && (
                  <ChatMessage isTyping />
                )}
                
                <div ref={messagesEndRef} />
              </Box>
              
              {/* Sample prompts */}
              {messages.length <= 1 && (
                <>
                  <Divider />
                  <SamplePrompts 
                    onSelectPrompt={handleSelectPrompt}
                    canPerformActions={capabilities?.userCanPerformActions}
                  />
                </>
              )}
              
              {/* Input */}
              <Box
                sx={{
                  p: 2,
                  borderTop: `1px solid ${theme.palette.divider}`,
                  bgcolor: 'background.paper',
                }}
              >
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    inputRef={inputRef}
                    fullWidth
                    size="small"
                    placeholder="Ask me anything..."
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading}
                    multiline
                    maxRows={3}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                  <IconButton
                    color="primary"
                    onClick={handleSendMessage}
                    disabled={!inputValue.trim() || isLoading}
                    sx={{
                      bgcolor: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        bgcolor: theme.palette.primary.dark,
                      },
                      '&.Mui-disabled': {
                        bgcolor: theme.palette.grey[300],
                        color: theme.palette.grey[500],
                      },
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SendIcon />
                    )}
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Floating Action Button */}
      <Zoom in={!isOpen}>
        <Fab
          color="primary"
          onClick={() => setIsOpen(true)}
          sx={{
            boxShadow: theme.shadows[8],
            transition: 'transform 0.2s ease-in-out',
            bgcolor: 'white',
            '&:hover': {
              transform: 'scale(1.05)',
              bgcolor: 'white',
            },
          }}
        >
          <Box
            component="img"
            src="/bolt-logo-chatbot.png"
            alt="Bolt AI"
            sx={{ width: 36, height: 36 }}
          />
        </Fab>
      </Zoom>
    </Box>
  );
};

export default AIChatWidget;

